
import React, { useState } from 'react';
import { useStore } from '../../context/StoreContext';
import { runGrowthTool } from '../../services/geminiService';
import { 
  Zap, Copy, Layout, PenTool, TrendingUp, 
  MessageSquare, FileText, ShoppingCart, 
  Heart, Star, Gift, Utensils, TicketPercent, Plus, Trash2, Tag
} from 'lucide-react';

const MarketingPage: React.FC = () => {
  const { business, coupons, createCoupon, deleteCoupon } = useStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState('');
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  
  const [newCoupon, setNewCoupon] = useState({ code: '', discount: 10 });

  const categories = [
    {
      name: 'Scripts de Fidelização',
      items: [
        { id: 'RECUPERAR_CARRINHO', icon: ShoppingCart, label: 'Abandonou Sacola' },
        { id: 'FIDELIDADE', icon: Heart, label: 'Pós-Venda / Feedback' },
        { id: 'UPSELL', icon: TrendingUp, label: 'Sugestão de Combo' },
        { id: 'BOAS_VINDAS', icon: MessageSquare, label: 'Boas-vindas' }
      ]
    },
    {
      name: 'Copy de Cardápio (IA)',
      items: [
        { id: 'DESC_BURGER', icon: Utensils, label: 'Hambúrguer Gourmet' },
        { id: 'DESC_PIZZA', icon: Star, label: 'Pizza Artesanal' },
        { id: 'DESC_DOCE', icon: Gift, label: 'Sobremesa Premium' }
      ]
    }
  ];

  const handleTool = async (id: string) => {
    setSelectedTool(id);
    setIsProcessing(true);
    setTimeout(async () => {
      const res = await runGrowthTool(id, { lead: { name: 'Cliente VIP', niche: 'Chef Selection' }, business });
      setResult(res);
      setIsProcessing(false);
    }, 400);
  };

  const handleCreateCoupon = async () => {
    if (!newCoupon.code) return;
    await createCoupon(newCoupon.code, newCoupon.discount);
    setNewCoupon({ code: '', discount: 10 });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-12 pb-20 animate-fade-in text-white">
      <header className="flex flex-col md:flex-row justify-between items-end gap-6 mb-16">
        <div>
          <h1 className="text-7xl font-black tracking-tighter uppercase leading-none italic">
            GROWTH <span className="text-rose-500">ENGINE</span>
          </h1>
          <p className="text-gray-500 font-bold uppercase text-[9px] tracking-[0.5em] mt-4 flex items-center gap-2">
            <TicketPercent className="w-4 h-4 text-rose-500" /> Fidelização, Cupons & Inteligência de Mercado
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-20">
          
          {/* Coupon Studio Section */}
          <section className="bg-white/[0.02] border border-white/5 rounded-[4rem] p-12 shadow-premium space-y-12">
             <div className="flex justify-between items-center">
                <h3 className="text-3xl font-black italic uppercase tracking-tighter flex items-center gap-4">
                   <TicketPercent className="w-8 h-8 text-rose-500" /> Coupon Studio
                </h3>
                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{coupons.length} Ativos</span>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 flex flex-col gap-4">
                   <label className="text-[8px] font-black text-gray-600 uppercase tracking-widest ml-4">Novo Código Promocional</label>
                   <div className="flex gap-4">
                      <input 
                        placeholder="EX: BEMVINDO20"
                        className="flex-1 bg-white/5 border border-white/10 rounded-3xl px-8 py-5 text-white font-black uppercase tracking-widest outline-none focus:border-rose-500 transition-all"
                        value={newCoupon.code}
                        onChange={e => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                      />
                      <input 
                        type="number"
                        placeholder="%"
                        className="w-32 bg-white/5 border border-white/10 rounded-3xl px-6 py-5 text-white font-black text-center outline-none focus:border-rose-500"
                        value={newCoupon.discount}
                        onChange={e => setNewCoupon({...newCoupon, discount: parseInt(e.target.value)})}
                      />
                   </div>
                </div>
                <button 
                  onClick={handleCreateCoupon}
                  className="mt-7 bg-white text-black rounded-3xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-rose-500 hover:text-white transition-all shadow-glow-rose"
                >
                   <Plus className="w-5 h-5" /> Criar Cupom
                </button>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {coupons.map(coupon => (
                   <div key={coupon.id} className="bg-black/40 border border-white/5 rounded-[2.5rem] p-8 flex items-center justify-between group">
                      <div className="flex items-center gap-6">
                         <div className="w-16 h-16 bg-rose-600/10 border border-rose-500/20 rounded-2xl flex items-center justify-center text-rose-500">
                            <Tag className="w-8 h-8" />
                         </div>
                         <div>
                            <p className="font-black text-xl text-white tracking-widest">{coupon.code}</p>
                            <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest">
                               Desconto de <span className="text-green-500">{coupon.discountPercentage}%</span> • {coupon.usageCount || 0} Usos
                            </p>
                         </div>
                      </div>
                      <button 
                        onClick={() => deleteCoupon(coupon.id)}
                        className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-gray-500 hover:bg-rose-600 hover:text-white transition-all"
                      >
                         <Trash2 className="w-4 h-4" />
                      </button>
                   </div>
                ))}
             </div>
          </section>

          {catMap(categories, handleTool, selectedTool)}
        </div>

        <div className="lg:col-span-4 sticky top-10 h-fit">
          <div className="bg-white/5 border border-white/10 rounded-[3rem] p-10 min-h-[450px] flex flex-col shadow-2xl backdrop-blur-3xl overflow-hidden relative group">
            <h3 className="text-2xl font-black mb-8 flex items-center gap-3 text-rose-400">Sugestão <Zap className="w-5 h-5" /></h3>

            {isProcessing ? (
              <div className="flex-1 flex flex-col items-center justify-center space-y-6">
                <div className="w-10 h-10 border-4 border-rose-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-[10px] font-black uppercase text-gray-500">Consultando Base...</p>
              </div>
            ) : result ? (
              <div className="flex-1 flex flex-col justify-between animate-fade-in relative z-10">
                <div className="bg-black/60 p-8 rounded-[2.5rem] border border-white/10 italic text-gray-300 font-medium leading-relaxed mb-8 shadow-inner overflow-y-auto max-h-[350px]">
                    "{result}"
                </div>
                <button onClick={() => {
                  navigator.clipboard.writeText(result);
                  alert('Copiado!');
                }} className="w-full bg-white text-black font-black py-6 rounded-3xl flex items-center justify-center gap-3 hover:bg-gray-200 shadow-2xl transition-all">
                  <Copy className="w-5 h-5" /> COPIAR CONTEÚDO
                </button>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center opacity-20 space-y-6">
                <Utensils className="w-16 h-16" />
                <p className="text-sm font-black uppercase tracking-widest max-w-[200px]">Escolha um script de marketing para o seu restaurante.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const catMap = (categories: any[], handleTool: any, selectedTool: any) => (
  <div className="space-y-16">
    {categories.map((cat: any) => (
      <div key={cat.name} className="space-y-6">
        <h3 className="text-xs font-black uppercase text-gray-500 tracking-[0.3em] pl-2 border-l-2 border-rose-500">{cat.name}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cat.items.map((item: any) => (
            <button 
              key={item.id} 
              onClick={() => handleTool(item.id)}
              className={`p-4 rounded-2xl border transition-all flex flex-col items-center gap-3 group h-28 justify-center text-center ${selectedTool === item.id ? 'bg-rose-600 border-rose-400 shadow-xl' : 'bg-white/5 border-white/5 hover:bg-white hover:text-black'}`}
            >
              <item.icon className={`w-5 h-5 ${selectedTool === item.id ? 'text-white' : 'text-rose-500 group-hover:text-black'}`} />
              <span className="text-[8px] font-black uppercase tracking-tighter leading-tight">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default MarketingPage;
